import { Plus, Trash2 } from 'lucide-react';
import { SCHEMA_MODELS } from '@/data/generated/schemaModels';
import {
  childDecls,
  helpLine,
  isRequired,
  missingChildren,
  resolveType,
  typeNameAtPath,
} from '@/lib/schema/model';
import type { SchemaElementDecl, SchemaModel } from '@/lib/schema/types';
import { parseXml, type XmlNode } from '@/lib/xml';
import { insertXmlChild, removeXmlNode, setXmlText, textAt } from '@/lib/schema/xmlMutate';
import { cn } from '@/lib/cn';
import { useT } from '@/i18n';

type Props = {
  short: string;
  xml: string;
  onXmlChange: (next: string) => void;
  helpMode: boolean;
  selectedPaths?: string[];
  onSelectPath?: (path: string) => void;
};

/**
 * Presence-driven schema form: walks current XML, shows required gaps and
 * + Add for optional XSD children. Help mode prints constraints from the model.
 */
export function SchemaForm({
  short,
  xml,
  onXmlChange,
  helpMode,
  selectedPaths = [],
  onSelectPath,
}: Props) {
  const t = useT();
  const model = SCHEMA_MODELS[short];
  const parsed = parseXml(xml);

  if (!model) {
    return (
      <div className="panel p-3 text-[12px] text-muted">{t('message.schemaUnavailable')}</div>
    );
  }

  if (!parsed.wellFormed || !parsed.root) {
    return (
      <div className="panel p-3 text-[12px] text-muted">
        {parsed.error ?? t('message.schemaNeedXml')}
      </div>
    );
  }

  const messageRoot =
    parsed.root.name === 'Document' ? parsed.root.children[0] : parsed.root;
  if (!messageRoot) {
    return <div className="panel p-3 text-[12px] text-muted">{t('message.schemaNeedXml')}</div>;
  }

  function apply(next: string | null) {
    if (next != null) onXmlChange(next);
  }

  return (
    <aside className="panel max-h-[calc(100dvh-8rem)] space-y-2 overflow-y-auto p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="eyebrow">{t('message.schemaForm')}</h2>
        <p className="font-mono text-[10px] text-muted">{model.messageId}</p>
      </div>
      {model.note && helpMode && (
        <p className="text-[11px] leading-snug text-muted">{model.note}</p>
      )}
      <SchemaNodeBlock
        model={model}
        node={messageRoot}
        path={messageRoot.name}
        typeName={model.documentChild.typeName}
        xml={xml}
        helpMode={helpMode}
        selectedPaths={selectedPaths}
        onSelectPath={onSelectPath}
        onSetText={(path, value) => apply(setXmlText(xml, path, value))}
        onAdd={(parentPath, name) => apply(insertXmlChild(xml, parentPath, name))}
        onRemove={(path) => apply(removeXmlNode(xml, path))}
        t={t}
      />
    </aside>
  );
}

function SchemaNodeBlock({
  model,
  node,
  path,
  typeName,
  xml,
  helpMode,
  selectedPaths,
  onSelectPath,
  onSetText,
  onAdd,
  onRemove,
  t,
  depth = 0,
}: {
  model: SchemaModel;
  node: XmlNode;
  path: string;
  typeName: string;
  xml: string;
  helpMode: boolean;
  selectedPaths: string[];
  onSelectPath?: (path: string) => void;
  onSetText: (path: string, value: string) => void;
  onAdd: (parentPath: string, name: string) => void;
  onRemove: (path: string) => void;
  t: ReturnType<typeof useT>;
  depth?: number;
}) {
  const typeDef = resolveType(model, typeName);
  const isLeaf =
    typeDef?.kind === 'simple' ||
    typeDef?.kind === 'simpleContent' ||
    (node.children.length === 0 && (typeDef == null || !typeDef.children?.length));

  const presentNames = node.children.map((c) => c.name);
  const { requiredMissing, optionalAbsent } = missingChildren(model, typeName, presentNames);
  const decls = childDecls(model, typeName);
  const highlighted = selectedPaths.some((p) => p === path || p.startsWith(`${path}/`) || p.startsWith(`${path}@`));

  if (isLeaf) {
    const value = textAt(xml, path) ?? node.text ?? '';
    const decl: SchemaElementDecl = {
      name: node.name,
      typeName,
      minOccurs: 1,
      maxOccurs: 1,
    };
    return (
      <div
        className={cn('space-y-1 rounded px-1 py-1', highlighted && 'bg-signal-soft ring-1 ring-signal')}
        style={{ marginLeft: Math.min(depth, 6) * 8 }}
      >
        <label className="flex flex-col gap-0.5">
          <span className="font-mono text-[11px]">
            <button
              type="button"
              className="hover:underline"
              onClick={() => onSelectPath?.(path)}
            >
              {node.name}
            </button>
          </span>
          {helpMode && (
            <span className="text-[10px] leading-snug text-muted">{helpLine(model, decl)}</span>
          )}
          <input
            value={value}
            onChange={(e) => onSetText(path, e.target.value)}
            onFocus={() => onSelectPath?.(path)}
            className="border border-rule bg-surface px-2 py-1 font-mono text-[12px] hover:border-ink"
          />
        </label>
        {depth > 0 && (
          <button
            type="button"
            onClick={() => onRemove(path)}
            className="inline-flex items-center gap-1 font-mono text-[10px] text-muted hover:text-ink"
          >
            <Trash2 size={10} aria-hidden />
            {t('message.schemaRemove')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn('space-y-1.5 rounded border border-rule-soft p-2', highlighted && 'ring-1 ring-signal')}
      style={{ marginLeft: Math.min(depth, 4) * 4 }}
    >
      <div className="flex flex-wrap items-center justify-between gap-1">
        <button
          type="button"
          className="font-mono text-[11px] font-semibold hover:underline"
          onClick={() => onSelectPath?.(path)}
        >
          {node.name}
        </button>
        {depth > 0 && (
          <button
            type="button"
            onClick={() => onRemove(path)}
            className="inline-flex items-center gap-1 font-mono text-[10px] text-muted hover:text-ink"
          >
            <Trash2 size={10} aria-hidden />
            {t('message.schemaRemove')}
          </button>
        )}
      </div>

      {node.children.map((child) => {
        const decl = decls.find((d) => d.name === child.name);
        const childType = decl?.typeName ?? typeNameAtPath(model, `${path}/${child.name}`) ?? child.name;
        const childPath = `${path}/${child.name}`;
        return (
          <div key={childPath + child.path}>
            {helpMode && decl && (
              <p className="mb-0.5 font-mono text-[10px] text-muted">{helpLine(model, decl)}</p>
            )}
            <SchemaNodeBlock
              model={model}
              node={child}
              path={childPath}
              typeName={childType}
              xml={xml}
              helpMode={helpMode}
              selectedPaths={selectedPaths}
              onSelectPath={onSelectPath}
              onSetText={onSetText}
              onAdd={onAdd}
              onRemove={onRemove}
              t={t}
              depth={depth + 1}
            />
          </div>
        );
      })}

      {requiredMissing.map((decl) => (
        <div key={`req-${decl.name}`} className="space-y-1 border border-dashed border-ochre/60 px-2 py-1.5">
          <p className="font-mono text-[11px] text-ochre">
            {decl.name}{' '}
            <span className="text-[10px] uppercase tracking-wider">{t('message.schemaRequiredMissing')}</span>
          </p>
          {helpMode && <p className="text-[10px] text-muted">{helpLine(model, decl)}</p>}
          <button
            type="button"
            onClick={() => onAdd(path, decl.name)}
            className="inline-flex items-center gap-1 border border-rule px-2 py-1 font-mono text-[10px] uppercase tracking-wider hover:border-ink"
          >
            <Plus size={10} aria-hidden />
            {t('message.schemaAdd', { name: decl.name })}
          </button>
        </div>
      ))}

      {optionalAbsent.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1">
          {optionalAbsent.map((decl) => (
            <button
              key={decl.name}
              type="button"
              title={helpMode ? helpLine(model, decl) : undefined}
              onClick={() => onAdd(path, decl.name)}
              className={cn(
                'inline-flex items-center gap-1 border border-rule px-2 py-1 font-mono text-[10px] text-muted hover:border-ink hover:text-ink',
                !isRequired(decl) && 'border-dashed',
              )}
            >
              <Plus size={10} aria-hidden />
              {t('message.schemaAdd', { name: decl.name })}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
